import { makeAutoObservable, runInAction } from 'mobx';
import { Network } from '@capacitor/network';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { dynamicIsland } from '../components/shared';

class GeneralStore {
  appName = 'Decade 2 Publishing';
  loading = true;
  isInternet = true;
  isServerError = false;
  serverErrorMessage = '';
  selectedFilter: string[] = [];
  appState: 'active' | 'background' = 'active';
  apiLevel = '';
  appBuildNumber = '';
  appVersionNumber = '';
  devicePlatform = '';

  constructor() {
    makeAutoObservable(this);
    this.initNetworkListener();
    this.initAppStateListener();
    this.initDeviceInfo();
  }

  async initNetworkListener() {
    try {
      const status = await Network.getStatus();
      runInAction(() => {
        this.isInternet = status.connected;
      });
    } catch {
      this.isInternet = true;
    }

    Network.addListener('networkStatusChange', (status) => {
      const wasOnline = this.isInternet;
      runInAction(() => {
        this.isInternet = status.connected;
      });
      // Surface only on transitions, not the initial state
      if (wasOnline && !status.connected) {
        dynamicIsland.show({
          kind: 'offline',
          title: 'You\'re offline',
          subtitle: 'Some features won\'t work until you reconnect',
          durationMs: 0, // sticky until back online
        });
      } else if (!wasOnline && status.connected) {
        dynamicIsland.show({
          kind: 'online',
          title: 'Back online',
          subtitle: 'You\'re reconnected',
        });
      }
    });
  }

  async initAppStateListener() {
    App.addListener('appStateChange', ({ isActive }) => {
      runInAction(() => {
        this.appState = isActive ? 'active' : 'background';
      });
    });
  }

  async initDeviceInfo() {
    try {
      const info = await Device.getInfo();
      // Device.getId() available for unique device identification if needed
      await Device.getId();
      runInAction(() => {
        this.devicePlatform = info.platform;
        this.appBuildNumber = (info as any).appBuild || (info as any).buildNumber || '';
        this.appVersionNumber = (info as any).appVersion || (info as any).versionNumber || '';
        this.apiLevel = (info as any).androidSDKVersion?.toString() || '';
      });
    } catch (e) {
      console.log('Device info error:', e);
    }
  }

  setLoading(val: boolean) {
    this.loading = val;
  }

  setIsInternet(val: boolean) {
    this.isInternet = val;
  }

  setServerError(val: boolean, message = '') {
    this.isServerError = val;
    this.serverErrorMessage = message;
  }

  setSelectedFilter(filters: string[]) {
    this.selectedFilter = filters;
  }

  clearFilters() {
    this.selectedFilter = [];
  }
}

export const generalStore = new GeneralStore();
